/**
 * Rounds — every round, one tab per game type, plus a round-id lookup.
 *
 * The list is the operational surface: one row per round (newest first), and
 * each round id links to the detail screen where an operator can drive it —
 * start, draw, or cancel — when the runner process is ever stuck or down.
 * Rounds are grouped into tabs, one per game type (Normal, Arif, and any type
 * added later), because a "Rounds" screen that mixes weekly Arif jackpots in
 * with minute-by-minute Normal rounds makes both lists unreadable.
 *
 * The id lookup sits at the top as a search: it validates the UUID *before*
 * navigating so a paste of the wrong thing shows a local message rather than
 * the server's 400. The detail route (`GET /admin/rounds/:id`) is the only
 * other round endpoint, so a lookup is how an operator reaches a specific
 * round the tabs don't surface (e.g. an ad-hoc round with no game type).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../api/client.ts';
import { can } from '../auth.ts';
import { at, etb, shortId } from '../format.ts';
import { href, navigate } from '../router.ts';
import {
  Banner,
  ConfirmButton,
  Empty,
  ErrorBanner,
  Id,
  ReasonForm,
  Row,
  Spinner,
  Status,
} from '../components/ui.tsx';
import type { AdminView, DrawResult, GameTypeView, RoundListItemView, RoundView } from '../api/types.ts';

const PAGE = 50;
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function Rounds({ slug }: { slug?: string }) {
  const [gameTypes, setGameTypes] = useState<GameTypeView[] | null>(null);
  const [activeSlug, setActiveSlug] = useState<string>(slug ?? 'all');

  useEffect(() => {
    void api
      .gameTypes()
      .then(({ gameTypes: next }) => setGameTypes(next))
      .catch(() => setGameTypes([]));
  }, []);

  // Future game types appear here automatically: the tabs are the API's list,
  // not a hardcoded Normal/Arif pair.
  const tabs = useMemo(() => {
    const types = gameTypes ?? [];
    return [
      { key: 'all', label: 'All', gameTypeId: null as string | null },
      ...types.map((gt) => ({ key: gt.slug, label: gt.name, gameTypeId: gt.id })),
    ];
  }, [gameTypes]);

  const activeTab =
    tabs.find((t) => t.key === activeSlug) ??
    (tabs[0] ?? { key: 'all', label: 'All', gameTypeId: null });

  // If the route slug no longer matches (e.g. after a type rename), fall back
  // to the first tab rather than rendering an empty list under a dead tab.
  useEffect(() => {
    if (gameTypes && !tabs.some((t) => t.key === activeSlug)) {
      setActiveSlug(tabs[0]?.key ?? 'all');
    }
  }, [gameTypes, tabs, activeSlug]);

  return (
    <section>
      <header className="pagehead">
        <h1>Rounds</h1>
      </header>

      <div className="card">
        <h2>Find a round</h2>
        <LookupForm />
      </div>

      <div className="tabs">
        {tabs.map((tab) => (
          <a
            key={tab.key}
            className={`tabs__tab${tab.key === activeTab.key ? ' tabs__tab--on' : ''}`}
            href={href({ name: 'rounds', slug: tab.key })}
            onClick={() => setActiveSlug(tab.key)}
          >
            {tab.label}
          </a>
        ))}
      </div>

      <RoundList gameTypeId={activeTab.gameTypeId} />
    </section>
  );
}

function LookupForm() {
  const [lookupId, setLookupId] = useState('');
  const [badId, setBadId] = useState(false);

  function open(): void {
    const trimmed = lookupId.trim();
    if (!UUID_RE.test(trimmed)) {
      setBadId(true);
      return;
    }
    setBadId(false);
    navigate({ name: 'round', id: trimmed });
  }

  return (
    <form
      className="reasonform"
      onSubmit={(e) => {
        e.preventDefault();
        open();
      }}
    >
      <input
        className="input"
        placeholder="Round id (a UUID)"
        value={lookupId}
        onChange={(e) => {
          setLookupId(e.target.value);
          setBadId(false);
        }}
      />
      <button type="submit" className="button" disabled={!lookupId.trim()}>
        Open
      </button>
      {badId ? (
        <p className="muted">
          That doesn't look like a round id — paste the full id from a round row
          or the lobby.
        </p>
      ) : null}
    </form>
  );
}

function RoundList({ gameTypeId }: { gameTypeId: string | null }) {
  const [rounds, setRounds] = useState<RoundListItemView[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [offset, setOffset] = useState(0);
  const [more, setMore] = useState(false);

  const load = useCallback(
    async (nextOffset: number) => {
      setError(null);
      try {
        const { rounds: page } = await api.rounds(gameTypeId, {
          limit: PAGE,
          offset: nextOffset,
        });
        setRounds((prev) => {
          const base = nextOffset === 0 ? [] : (prev ?? []);
          return [...base, ...page];
        });
        setMore(page.length === PAGE);
        setOffset(nextOffset + page.length);
      } catch (err) {
        setError(err);
      }
    },
    [gameTypeId],
  );

  // Switching tabs resets to the top of that tab's list.
  useEffect(() => {
    setRounds(null);
    setOffset(0);
    setMore(false);
    void load(0);
  }, [load]);

  if (error) {
    return (
      <section>
        <ErrorBanner error={error} />
      </section>
    );
  }
  if (rounds === null) {
    return <Spinner />;
  }

  return (
    <>
      {rounds.length === 0 ? (
        <Empty>No rounds here yet.</Empty>
      ) : (
        <table className="table" role="table">
          <thead role="rowgroup">
            <tr role="row">
              <th scope="col" role="columnheader">Round</th>
              <th scope="col" role="columnheader">Status</th>
              <th scope="col" role="columnheader">Players</th>
              <th scope="col" role="columnheader">Cards</th>
              <th scope="col" role="columnheader">Prize</th>
              <th scope="col" role="columnheader">Started</th>
              <th scope="col" role="columnheader">Ended</th>
              <th scope="col" role="columnheader">Winner</th>
            </tr>
          </thead>
          <tbody role="rowgroup">
            {rounds.map((r) => (
              <tr role="row" key={r.id}>
                <td role="cell" data-label="Round">
                  <a className="link" href={href({ name: 'round', id: r.id })}>
                    {shortId(r.id)}
                  </a>
                </td>
                <td role="cell" data-label="Status">
                  <Status value={r.status} />
                </td>
                <td role="cell" data-label="Players">
                  {r.playerCount} <span className="muted">/ min {r.minPlayers}</span>
                </td>
                <td role="cell" data-label="Cards">{r.cardCount}</td>
                <td role="cell" data-label="Prize">{r.fixedPrizeAmount !== null ? etb(r.fixedPrizeAmount) : etb(r.prizePool)}</td>
                <td role="cell" data-label="Started">{at(r.startsAt)}</td>
                <td role="cell" data-label="Ended">{at(r.endedAt)}</td>
                <td role="cell" data-label="Winner">
                  {r.winnerUserName !== null ? (
                    <span className="nowrap" title={r.winnerUserId ?? undefined}>
                      {r.winnerUserName}
                    </span>
                  ) : (
                    <code className="muted">{shortId(r.winnerUserId)}</code>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {more ? (
        <button type="button" className="button" onClick={() => void load(offset)}>
          Load more
        </button>
      ) : null}
    </>
  );
}

export function RoundDetail({ id, me }: { id: string; me: AdminView }) {
  const [round, setRound] = useState<RoundView | null>(null);
  const [lastDraw, setLastDraw] = useState<DrawResult | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError(null);
    try {
      setRound(await api.round(id));
    } catch (err) {
      setError(err);
    }
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(fn: () => Promise<unknown>): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  if (!round) {
    return (
      <section>
        <ErrorBanner error={error} />
        {!error ? <Spinner /> : null}
      </section>
    );
  }

  return (
    <section className="detail">
      <header className="pagehead">
        <a className="link back" href={href({ name: 'rounds' })}>
          ‹ Rounds
        </a>
        <h1>{etb(round.entryFee)} entry</h1>
        <Status value={round.status} />
      </header>

      <ErrorBanner error={error} />

      <div className="detail__facts">
        <Row label="Round">
          <Id value={round.id} />
        </Row>
        <Row label="Pattern">{round.patternId}</Row>
        <Row label="Players">
          {round.playerCount ?? '—'} <span className="muted">(min {round.minPlayers})</span>
        </Row>
        <Row label="Cards">{round.cardCount ?? '—'}</Row>
        <Row label="Prize pool">{etb(round.prizePool)}</Row>
        <Row label="Commission">
          {round.commissionBps === null ? '—' : `${round.commissionBps / 100}%`}
        </Row>
        <Row label="Draw interval">{round.drawIntervalMs} ms</Row>
        <Row label="Join closes">{at(round.joinClosesAt)}</Row>
        <Row label="Started">{at(round.startsAt)}</Row>
        <Row label="Ended">{at(round.endedAt)}</Row>
        <Row label="Winner">
          <Id value={round.winnerUserId} />
        </Row>
      </div>

      {/*
        The commitment is published when the round goes live; the seed only
        once it is over. Showing them side by side is the point of §4 — an
        auditor checks that this seed hashes to that commitment.
      */}
      <div className="card">
        <h2>Fairness</h2>
        <Row label="Commitment">
          <code className="hash">{round.seedCommitment ?? '—'}</code>
        </Row>
        <Row label="Revealed seed">
          <code className="hash">{round.rngSeed ?? 'sealed until the round ends'}</code>
        </Row>
      </div>

      {lastDraw ? (
        <Banner kind="ok">
          Drew <strong>{lastDraw.number}</strong> (#{lastDraw.drawIndex}).
          {lastDraw.roundFinished ? ' The round is over.' : null}
          {lastDraw.winner ? ` Winner paid ${etb(lastDraw.winner.amount)}.` : null}
        </Banner>
      ) : null}

      <div className="actions">
        {round.status === 'waiting' ? (
          <ConfirmButton
            className="button button--primary"
            confirm="Start this round? Joining closes immediately."
            disabled={busy || !can(me.role, 'round.start')}
            onConfirm={() => void act(() => api.startRound(round.id))}
          >
            Start
          </ConfirmButton>
        ) : null}

        {round.status === 'live' ? (
          <button
            type="button"
            className="button"
            disabled={busy || !can(me.role, 'round.start')}
            onClick={() =>
              void act(async () => {
                setLastDraw(await api.drawNext(round.id));
              })
            }
          >
            Draw next number
          </button>
        ) : null}

        {round.status === 'waiting' || round.status === 'starting' || round.status === 'live' ? (
          <ReasonForm
            action="Cancel round"
            placeholder="Why is this round being cancelled?"
            danger
            disabled={busy || !can(me.role, 'round.cancel')}
            onSubmit={(reason) => void act(() => api.cancelRound(round.id, reason))}
          />
        ) : null}
      </div>

      {round.status === 'waiting' || round.status === 'starting' || round.status === 'live' ? (
        <p className="muted">
          Cancelling refunds every stake in full.
        </p>
      ) : null}
    </section>
  );
}